import { dom } from '../dom'
import { app } from '../appState'

import UIkit from 'uikit'

export const modal = {
  /**
   * Show modal dialog for given id.
   * @param {string} id Modal Id.
   */
  show: (id) => UIkit.modal(id, { bgClose: false, stack: true }).show(),

  /**
   * Hide modal dialog for given id.
   * @param {string} id Modal Id.
   */
  hide: (id) => UIkit.modal(id).hide(),

  /**
   * Center the dialog of a modal. Uses requestAnimationFrame to center precisely.
   * @param {string|HTMLElement} target Modal Id or modal dialog HTMLElement.
   */
  center: (target) => {
    const element = typeof target === 'string' ? document.querySelector(`${target} .uk-modal-dialog`) : target

    if (!element) return

    requestAnimationFrame(() => {
      Object.assign(element.style, {
        left: `${(window.innerWidth - element.offsetWidth) / 2}px`,
        top: `${(window.innerHeight - element.offsetHeight) / 2}px`,
        visibility: ''
      })
    })
  }
}

/**
 * Show error dialog.
 * @param {string} title Title of dialog box.
 * @param {string} error Error message to show.
 * @param {boolean} [allowHtml=false] Whether to render error message as HTML.
 */
export function showError(title, error, allowHtml = false) {
  dom.errTitle.textContent = title

  if (allowHtml) {
    dom.errMsg.innerHTML = error
  } else {
    dom.errMsg.textContent = error
  }

  modal.show('#dialogError')
}

/**
 * Show confirmation dialog.
 * @param {string} msg Confirmation message to show.
 * @param {function} action Function to run upon selecting Yes.
 */
export function confirm(msg, action) {
  dom.confirmMsg.textContent = msg

  const syncContainer = dom.el('#confirmKeepSyncContainer')
  if (syncContainer) {
    syncContainer.style.display = 'none'
  }

  modal.show('#dialogConfirm')

  const yesAction = (event) => {
    if (dom.confirmYes) dom.confirmYes.blur()

    event.stopPropagation()
    UIkit.modal('#dialogConfirm').hide()
    setTimeout(action, 50)
  }

  dom.confirmYes.onclick = yesAction
}

/**
 * Show app notifications.
 * @param {string} msg Notification to show.
 * @param {string} [stat='primary'] Notification status: primary | success | warning | danger
 */
export function notify(msg, stat = 'primary') {
  UIkit.notification({
    message: msg,
    pos: app.settings.notifyLocation,
    status: stat,
    timeout: +app.settings.notifyDuration
  })
}

/**
 * Makes a UIkit modal dialog draggable by its title/header.
 * @param {HTMLElement} modalElement The root modal element containing the dialog and title.
 */
function makeModalDraggable(modalElement) {
  const dialog = modalElement.querySelector('.uk-modal-dialog')
  const header = dialog?.querySelector('.uk-modal-title')

  if (!dialog || !header) return

  UIkit.util.on(modalElement, 'beforeshow', (e) => {
    if (e.target !== modalElement) return
    if (dom.el('#dialogUdfu')?.classList.contains('uk-open')) return

    Object.assign(dialog.style, { visibility: 'hidden', display: 'block', position: 'absolute' })

    modal.center(dialog)
  })

  let dragging = false
  let offsetX = 0
  let offsetY = 0

  const move = (e) => {
    if (!dragging) return

    dialog.style.left = `${Math.max(10, Math.min(e.clientX - offsetX, window.innerWidth - dialog.offsetWidth - 10))}px`
    dialog.style.top = `${Math.max(40, Math.min(e.clientY - offsetY, window.innerHeight - dialog.offsetHeight - 10))}px`
  }

  const up = () => {
    dragging = false
    document.removeEventListener('mousemove', move)
    document.removeEventListener('mouseup', up)
  }

  header.addEventListener('mousedown', (e) => {
    offsetX = e.clientX - dialog.offsetLeft
    offsetY = e.clientY - dialog.offsetTop
    dragging = true

    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
  })

  header.addEventListener('dblclick', () => modal.center(dialog))
}

// Make all modals draggable
dom.els('.modal').forEach(makeModalDraggable)
